import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import DuplicateWarningIcon from "@app/component/DuplicateWarningIcon";
import LeadFinderItemActions from "@app/component/LeadItem/components/LeadFinderItemActions";
import LeadItemHours from "@app/component/LeadItem/components/LeadItemHours";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import OverlayView from "@app/component/map/FeaturedMap/essentials/OverlayView";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import { setLeadAnnotation, showDuplicates } from "@app/scene/map/store/leadFinder/actions";
import { toggleSelectedItem } from "@app/scene/map/store/mapMode/actions";
import { getFilteredSelectedRecords, getLeadAnnotation } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { leadIdGetter } from "@app/util/map/idGetters";

import styles from "./LeadAnnotation.module.scss";

interface Props {
  annotation?: string;
  leads: Lead[];
  onSetAnnotation: typeof setLeadAnnotation;
  onWarningClick: typeof showDuplicates;
  selection: Set<string>;
  toggleSelectedItem: typeof toggleSelectedItem;
  zIndex?: number;
}

const LeadAnnotation: React.FC<Props> = ({
  annotation,
  leads,
  onSetAnnotation,
  onWarningClick,
  selection,
  toggleSelectedItem,
  zIndex = 9001, // guaranteed to override sidebar and navbar
}) => {
  const analytics = useExtendedAnalytics("Lead annotation", useAnalytics());
  const intl = useIntl();
  const { map } = useMap();

  const lead = useMemo(() => {
    if (annotation) {
      return leads?.find((lead) => leadIdGetter(lead) === annotation);
    }

    return undefined;
  }, [annotation, leads]);

  const position: google.maps.LatLngLiteral | undefined = useMemo(() => {
    if (lead?.geometry) {
      return lead.geometry.location;
    }
  }, [lead]);

  const handleClearPosition = useCallback(() => {
    if (lead) {
      onSetAnnotation(undefined);
    }
  }, [lead, onSetAnnotation]);

  const handleSelect = useCallback(() => {
    if (lead) {
      toggleSelectedItem({ entity: leadIdGetter(lead), value: true });
      analytics.clicked(["Select lead"]);
    }
  }, [analytics, lead, toggleSelectedItem]);

  const handleUnselect = useCallback(() => {
    if (lead) {
      toggleSelectedItem({ entity: leadIdGetter(lead), value: false });
      analytics.clicked(["Unselect lead"]);
    }
  }, [analytics, lead, toggleSelectedItem]);

  const isVisible = map && position && lead;
  const isSelected = annotation ? selection.has(annotation) : false;

  return isVisible ? (
    <AnalyticsContext.Provider value={analytics}>
      <OverlayView
        arrowClassName={styles.arrow}
        borderClassName={styles.border}
        map={map}
        onClickOutside={handleClearPosition}
        position={position}
        smartPosition
        zIndex={zIndex}
      >
        <div className={cn(styles.container, { [styles.selected]: isSelected })}>
          <div className={styles.arrow} />
          <div className={styles.border} />

          <Interactive>
            <div className={styles.body}>
              <div className={styles.info}>
                <div className={styles.details}>
                  <div className={styles.name}>{lead.name}</div>
                  <div className={styles.address}>{lead.formatted_address}</div>
                </div>

                <DuplicateWarningIcon entity={lead} onClick={onWarningClick} />
              </div>

              <LeadItemHours openingHours={lead.opening_hours} />
              <LeadFinderItemActions entity={lead} />

              <div className={styles.action}>
                {isSelected ? (
                  <Button block className={styles.btnUndo} onClick={handleUnselect}>
                    {intl.formatMessage({
                      id: "map.tool.leadFinder.annotation.undoSelection",
                      defaultMessage: "Undo selection",
                      description: "Undo selection - button title",
                    })}
                  </Button>
                ) : (
                  <Button block className={styles.btn} onClick={handleSelect}>
                    {intl.formatMessage({
                      id: "map.tool.leadFinder.annotation.selectThisLead",
                      defaultMessage: "Select this lead",
                      description: "Select this lead - button title",
                    })}
                  </Button>
                )}
              </div>
            </div>
          </Interactive>
        </div>
      </OverlayView>
    </AnalyticsContext.Provider>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  annotation: getLeadAnnotation(state),
  selection: new Set(getFilteredSelectedRecords(state).map(({ place_id }) => place_id)),
});

const mapDispatchToProps = {
  onSetAnnotation: setLeadAnnotation,
  onWarningClick: showDuplicates,
  toggleSelectedItem,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadAnnotation);
